import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    Card, Table, Tag, Button, Space, Row, Col, Input,
    Typography, Avatar, Spin, Empty, Statistic, message, Select, DatePicker, Dropdown
} from 'antd';
import {
    ReloadOutlined,
    CheckCircleOutlined, ClockCircleOutlined,
    PhoneOutlined, TeamOutlined, CrownOutlined,
    SearchOutlined, EyeOutlined,
    WalletOutlined, DollarOutlined,
    DownloadOutlined, FileExcelOutlined,
    FilterOutlined
} from '@ant-design/icons';
import affiliatesService from '../../common/services/affiliates.service';
import dayjs from 'dayjs';
import XLSX from 'xlsx-js-style';

const { Title, Text } = Typography;
const { RangePicker } = DatePicker;

const RevenuesPaymentStatusPage = () => {
    const navigate = useNavigate();
    const [loading, setLoading] = useState(true);
    const [data, setData] = useState([]);
    const [stats, setStats] = useState({});
    const [searchText, setSearchText] = useState('');
    const [typeFilter, setTypeFilter] = useState(null); // affiliate, ambassador, all
    const [paymentFilter, setPaymentFilter] = useState(null); // paid, pending, partial, all
    const [cityFilter, setCityFilter] = useState(null);
    const [dateRange, setDateRange] = useState(null);
    const [exporting, setExporting] = useState(false);

    const fetchData = useCallback(async () => {
        setLoading(true);
        try {
            const params = {};
            if (typeFilter) params.type = typeFilter;
            if (paymentFilter) params.paymentStatus = paymentFilter;
            if (dateRange && dateRange[0] && dateRange[1]) {
                params.startDate = dateRange[0].format('YYYY-MM-DD');
                params.endDate = dateRange[1].format('YYYY-MM-DD');
            }

            const response = await affiliatesService.getPaymentStatus(params);
            if (response.success) {
                setData(response.data || []);
                setStats(response.stats || {});
            } else {
                message.error(response.message || 'Erreur lors du chargement');
            }
        } catch (error) {
            console.error('Erreur chargement état des paiements:', error);
            message.error('Erreur lors du chargement des données');
        } finally {
            setLoading(false);
        }
    }, [typeFilter, paymentFilter, dateRange]);

    useEffect(() => {
        fetchData();
    }, [fetchData]);

    // Extraire les villes uniques
    const cityOptions = [...new Map(
        data
            .filter(d => d.city?.name)
            .map(d => [d.city.name, { label: d.city.name, value: d.city.name }])
    ).values()].sort((a, b) => a.label.localeCompare(b.label));

    // Filtrer par ville et recherche texte
    const filteredData = data.filter(item => {
        if (cityFilter && item.city?.name !== cityFilter) return false;
        if (!searchText) return true;
        const search = searchText.toLowerCase();
        return (
            (item.firstName?.toLowerCase() || '').includes(search) ||
            (item.lastName?.toLowerCase() || '').includes(search) ||
            (item.phone || '').includes(search)
        );
    });

    // Préparer les données d'export
    const prepareExportData = () => {
        return filteredData.map(item => ({
            'Type': item.type === 'affiliate' ? 'Affilié' : 'Ambassadeur',
            'Nom': `${item.firstName || ''} ${item.lastName || ''}`.trim(),
            'Téléphone': item.phone || '',
            'Ville': item.city?.name || '-',
            'Chauffeurs': item.driversCount || 0,
            'Payés': item.driversPaid || 0,
            'En attente': item.driversPending || 0,
            'Partiels': item.driversPartial || 0,
            'Gains totaux (F)': item.totalGains || 0,
            'Payé (F)': item.totalPaid || 0,
            'Reste à payer (F)': item.totalPending || 0,
            'Statut global': item.overallPaymentStatus === 'paid' ? 'Payé' :
                item.overallPaymentStatus === 'partial' ? 'Partiel' : 'En attente'
        }));
    };

    // Export CSV
    const exportToCSV = () => {
        setExporting(true);
        try {
            const exportData = prepareExportData();
            if (exportData.length === 0) {
                message.warning('Aucune donnée à exporter');
                return;
            }

            const titleLine = `État des paiements - ${typeFilter === 'affiliate' ? 'Affiliés' : typeFilter === 'ambassador' ? 'Ambassadeurs' : 'Tous'}`;
            let periodLine = '';
            if (dateRange && dateRange[0] && dateRange[1]) {
                periodLine = `Période: ${dateRange[0].format('DD/MM/YYYY')} - ${dateRange[1].format('DD/MM/YYYY')}`;
            } else {
                periodLine = `Exporté le: ${dayjs().format('DD/MM/YYYY à HH:mm')}`;
            }

            const headers = Object.keys(exportData[0]);
            const csvContent = [
                titleLine,
                periodLine,
                '',
                headers.join(';'),
                ...exportData.map(row => headers.map(h => `"${row[h]}"`).join(';'))
            ].join('\n');

            const blob = new Blob(['\ufeff' + csvContent], { type: 'text/csv;charset=utf-8;' });
            const link = document.createElement('a');
            const url = URL.createObjectURL(blob);
            const filename = `etat_paiements_${dayjs().format('YYYY-MM-DD_HHmm')}.csv`;

            link.setAttribute('href', url);
            link.setAttribute('download', filename);
            link.style.visibility = 'hidden';
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);

            message.success(`Export CSV réussi: ${filename}`);
        } catch (error) {
            console.error('Erreur export CSV:', error);
            message.error('Erreur lors de l\'export CSV');
        } finally {
            setExporting(false);
        }
    };

    // Export Excel
    const exportToExcel = () => {
        setExporting(true);
        try {
            const exportData = prepareExportData();
            if (exportData.length === 0) {
                message.warning('Aucune donnée à exporter');
                return;
            }

            const titleStyle = {
                font: { bold: true, sz: 14, color: { rgb: "000000" } },
                alignment: { horizontal: "left", vertical: "center" }
            };
            const periodStyle = {
                font: { bold: true, sz: 11, color: { rgb: "555555" } },
                alignment: { horizontal: "left", vertical: "center" }
            };
            const headerStyle = {
                font: { bold: true, sz: 11, color: { rgb: "FFFFFF" } },
                fill: { fgColor: { rgb: "722ED1" } },
                alignment: { horizontal: "center", vertical: "center" },
                border: {
                    top: { style: "thin", color: { rgb: "000000" } },
                    bottom: { style: "thin", color: { rgb: "000000" } },
                    left: { style: "thin", color: { rgb: "000000" } },
                    right: { style: "thin", color: { rgb: "000000" } }
                }
            };
            const cellStyle = {
                border: {
                    top: { style: "thin", color: { rgb: "CCCCCC" } },
                    bottom: { style: "thin", color: { rgb: "CCCCCC" } },
                    left: { style: "thin", color: { rgb: "CCCCCC" } },
                    right: { style: "thin", color: { rgb: "CCCCCC" } }
                },
                alignment: { vertical: "center" }
            };

            const titleText = `ÉTAT DES PAIEMENTS - ${typeFilter === 'affiliate' ? 'AFFILIÉS' : typeFilter === 'ambassador' ? 'AMBASSADEURS' : 'TOUS'}`;
            const periodText = dateRange && dateRange[0] && dateRange[1]
                ? `PÉRIODE: ${dateRange[0].format('DD/MM/YYYY')} - ${dateRange[1].format('DD/MM/YYYY')}`
                : `EXPORTÉ LE: ${dayjs().format('DD/MM/YYYY à HH:mm').toUpperCase()}`;

            const headerRows = [
                [{ v: titleText, s: titleStyle }],
                [{ v: periodText, s: periodStyle }],
                []
            ];

            const worksheet = XLSX.utils.aoa_to_sheet(headerRows);

            const headers = Object.keys(exportData[0]);
            const dataWithHeaders = [
                headers.map(h => ({ v: h.toUpperCase(), s: headerStyle })),
                ...exportData.map(row => headers.map(h => ({ v: row[h], s: cellStyle })))
            ];

            XLSX.utils.sheet_add_aoa(worksheet, dataWithHeaders, { origin: 'A4' });

            const workbook = XLSX.utils.book_new();
            XLSX.utils.book_append_sheet(workbook, worksheet, 'État des paiements');

            const colWidths = headers.map(key => ({
                wch: Math.max(key.length, ...exportData.map(row => String(row[key] || '').length), 12) + 2
            }));
            worksheet['!cols'] = colWidths;

            const colCount = headers.length;
            worksheet['!merges'] = [
                { s: { r: 0, c: 0 }, e: { r: 0, c: colCount - 1 } },
                { s: { r: 1, c: 0 }, e: { r: 1, c: colCount - 1 } }
            ];

            worksheet['!rows'] = [
                { hpt: 25 },
                { hpt: 20 },
                { hpt: 15 },
                { hpt: 22 }
            ];

            const filename = `etat_paiements_${dayjs().format('YYYY-MM-DD_HHmm')}.xlsx`;
            XLSX.writeFile(workbook, filename);

            message.success(`Export Excel réussi: ${filename}`);
        } catch (error) {
            console.error('Erreur export Excel:', error);
            message.error('Erreur lors de l\'export Excel');
        } finally {
            setExporting(false);
        }
    };

    const exportMenuItems = [
        {
            key: 'csv',
            label: 'Exporter en CSV',
            icon: <DownloadOutlined />,
            onClick: exportToCSV
        },
        {
            key: 'excel',
            label: 'Exporter en Excel',
            icon: <FileExcelOutlined />,
            onClick: exportToExcel
        }
    ];

    const columns = [
        {
            title: 'Bénéficiaire',
            key: 'beneficiary',
            width: 250,
            fixed: 'left',
            render: (_, record) => (
                <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                    <Avatar
                        size={40}
                        style={{
                            background: record.type === 'affiliate' ? '#1890ff' : '#722ed1',
                            flexShrink: 0
                        }}
                        icon={record.type === 'affiliate' ? <TeamOutlined /> : <CrownOutlined />}
                    />
                    <div>
                        <div style={{ fontWeight: 600, color: '#262626', fontSize: 13 }}>
                            {record.firstName} {record.lastName}
                        </div>
                        <div style={{ fontSize: 11, color: '#8c8c8c' }}>
                            <PhoneOutlined style={{ marginRight: 4 }} />
                            {record.phone}
                        </div>
                        <Tag
                            color={record.type === 'affiliate' ? 'blue' : 'purple'}
                            style={{ margin: '2px 0 0 0', borderRadius: 8, fontSize: 10 }}
                        >
                            {record.type === 'affiliate' ? 'Affilié' : 'Ambassadeur'}
                        </Tag>
                    </div>
                </div>
            )
        },
        {
            title: 'Ville',
            key: 'city',
            width: 100,
            render: (_, record) => (
                <Tag style={{ margin: 0, fontSize: 11 }}>
                    {record.city?.name || '-'}
                </Tag>
            )
        },
        {
            title: 'Chauffeurs',
            key: 'drivers',
            width: 120,
            align: 'center',
            render: (_, record) => (
                <div>
                    <div style={{ fontWeight: 700, fontSize: 16, color: '#262626' }}>
                        {record.driversCount || 0}
                    </div>
                    <div style={{ fontSize: 10, color: '#8c8c8c' }}>
                        <span style={{ color: '#52c41a' }}>{record.driversPaid || 0}✓</span>
                        {' / '}
                        <span style={{ color: '#faad14' }}>{record.driversPending || 0}⏳</span>
                        {record.driversPartial > 0 && (
                            <span style={{ color: '#1890ff' }}> / {record.driversPartial}⚡</span>
                        )}
                    </div>
                </div>
            )
        },
        {
            title: 'Gains totaux',
            key: 'totalGains',
            width: 130,
            align: 'right',
            render: (_, record) => (
                <div style={{ fontWeight: 600, color: '#262626', fontSize: 14 }}>
                    {(record.totalGains || 0).toLocaleString()} F
                </div>
            ),
            sorter: (a, b) => (a.totalGains || 0) - (b.totalGains || 0)
        },
        {
            title: 'Payé',
            key: 'totalPaid',
            width: 120,
            align: 'right',
            render: (_, record) => (
                <div style={{ fontWeight: 600, color: '#52c41a', fontSize: 14 }}>
                    {(record.totalPaid || 0).toLocaleString()} F
                </div>
            ),
            sorter: (a, b) => (a.totalPaid || 0) - (b.totalPaid || 0)
        },
        {
            title: 'Reste à payer',
            key: 'totalPending',
            width: 130,
            align: 'right',
            render: (_, record) => (
                <div style={{
                    fontWeight: 700,
                    color: (record.totalPending || 0) > 0 ? '#faad14' : '#52c41a',
                    fontSize: 14
                }}>
                    {(record.totalPending || 0).toLocaleString()} F
                </div>
            ),
            sorter: (a, b) => (a.totalPending || 0) - (b.totalPending || 0),
            defaultSortOrder: 'descend'
        },
        {
            title: 'Statut',
            key: 'status',
            width: 100,
            align: 'center',
            render: (_, record) => {
                const status = record.overallPaymentStatus;
                const config = {
                    paid: { color: 'green', text: 'Payé', icon: <CheckCircleOutlined /> },
                    pending: { color: 'orange', text: 'En attente', icon: <ClockCircleOutlined /> },
                    partial: { color: 'blue', text: 'Partiel', icon: <WalletOutlined /> },
                    none: { color: 'default', text: '-', icon: null }
                };
                const c = config[status] || config.none;
                return (
                    <Tag color={c.color} icon={c.icon} style={{ borderRadius: 10 }}>
                        {c.text}
                    </Tag>
                );
            },
            filters: [
                { text: 'Payé', value: 'paid' },
                { text: 'En attente', value: 'pending' },
                { text: 'Partiel', value: 'partial' }
            ],
            onFilter: (value, record) => record.overallPaymentStatus === value
        },
        {
            title: 'Actions',
            key: 'actions',
            width: 80,
            align: 'center',
            fixed: 'right',
            render: (_, record) => (
                <Button
                    type="text"
                    icon={<EyeOutlined />}
                    onClick={() => navigate(`/dashboard/revenues/${record.type}/${record._id}`)}
                    style={{ color: '#1890ff' }}
                />
            )
        }
    ];

    // Calculs pour les statistiques
    const totalGains = (stats.totalGainsAffiliates || 0) + (stats.totalGainsAmbassadors || 0);
    const totalPaid = (stats.totalPaidAffiliates || 0) + (stats.totalPaidAmbassadors || 0);
    const totalPending = (stats.totalPendingAffiliates || 0) + (stats.totalPendingAmbassadors || 0);

    if (loading && data.length === 0) {
        return (
            <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
                <Spin size="large" />
            </div>
        );
    }

    return (
        <div style={{ padding: '16px 24px', maxWidth: 1600, margin: '0 auto' }}>
            {/* Header */}
            <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                marginBottom: 20,
                flexWrap: 'wrap',
                gap: 12
            }}>
                <div>
                    <Title level={4} style={{ margin: 0, color: '#262626' }}>
                        <DollarOutlined style={{ marginRight: 8, color: '#722ed1' }} />
                        État des paiements
                    </Title>
                    <Text type="secondary" style={{ fontSize: 12 }}>
                        Suivi des paiements aux affiliés et ambassadeurs
                    </Text>
                </div>
                <Space wrap>
                    <Dropdown menu={{ items: exportMenuItems }} placement="bottomRight">
                        <Button icon={<DownloadOutlined />} loading={exporting} style={{ borderRadius: 8 }}>
                            Exporter
                        </Button>
                    </Dropdown>
                    <Button
                        icon={<ReloadOutlined />}
                        onClick={fetchData}
                        loading={loading}
                        style={{ borderRadius: 8 }}
                    >
                        Actualiser
                    </Button>
                </Space>
            </div>

            {/* Stats Cards */}
            <Row gutter={[16, 16]} style={{ marginBottom: 20 }}>
                <Col xs={12} sm={8} md={6} lg={4}>
                    <Card style={{ borderRadius: 12, height: '100%' }} bodyStyle={{ padding: 16, textAlign: 'center' }}>
                        <TeamOutlined style={{ fontSize: 22, color: '#1890ff', marginBottom: 4 }} />
                        <Statistic
                            value={stats.totalAffiliates || 0}
                            valueStyle={{ fontSize: 20, fontWeight: 700, color: '#262626' }}
                        />
                        <div style={{ fontSize: 11, color: '#8c8c8c', marginTop: -4 }}>Affiliés</div>
                    </Card>
                </Col>
                <Col xs={12} sm={8} md={6} lg={4}>
                    <Card style={{ borderRadius: 12, height: '100%' }} bodyStyle={{ padding: 16, textAlign: 'center' }}>
                        <CrownOutlined style={{ fontSize: 22, color: '#722ed1', marginBottom: 4 }} />
                        <Statistic
                            value={stats.totalAmbassadors || 0}
                            valueStyle={{ fontSize: 20, fontWeight: 700, color: '#262626' }}
                        />
                        <div style={{ fontSize: 11, color: '#8c8c8c', marginTop: -4 }}>Ambassadeurs</div>
                    </Card>
                </Col>
                <Col xs={12} sm={8} md={6} lg={4}>
                    <Card style={{ borderRadius: 12, height: '100%' }} bodyStyle={{ padding: 16, textAlign: 'center' }}>
                        <WalletOutlined style={{ fontSize: 22, color: '#faad14', marginBottom: 4 }} />
                        <Statistic
                            value={totalGains}
                            suffix="F"
                            valueStyle={{ fontSize: 18, fontWeight: 700, color: '#262626' }}
                        />
                        <div style={{ fontSize: 11, color: '#8c8c8c', marginTop: -4 }}>Gains totaux</div>
                    </Card>
                </Col>
                <Col xs={12} sm={8} md={6} lg={4}>
                    <Card style={{ borderRadius: 12, height: '100%' }} bodyStyle={{ padding: 16, textAlign: 'center' }}>
                        <CheckCircleOutlined style={{ fontSize: 22, color: '#52c41a', marginBottom: 4 }} />
                        <Statistic
                            value={totalPaid}
                            suffix="F"
                            valueStyle={{ fontSize: 18, fontWeight: 700, color: '#52c41a' }}
                        />
                        <div style={{ fontSize: 11, color: '#8c8c8c', marginTop: -4 }}>Total payé</div>
                    </Card>
                </Col>
                <Col xs={12} sm={8} md={6} lg={4}>
                    <Card style={{ borderRadius: 12, height: '100%' }} bodyStyle={{ padding: 16, textAlign: 'center' }}>
                        <ClockCircleOutlined style={{ fontSize: 22, color: '#faad14', marginBottom: 4 }} />
                        <Statistic
                            value={totalPending}
                            suffix="F"
                            valueStyle={{ fontSize: 18, fontWeight: 700, color: '#faad14' }}
                        />
                        <div style={{ fontSize: 11, color: '#8c8c8c', marginTop: -4 }}>Reste à payer</div>
                    </Card>
                </Col>
                <Col xs={12} sm={8} md={6} lg={4}>
                    <Card style={{ borderRadius: 12, height: '100%' }} bodyStyle={{ padding: 16, textAlign: 'center' }}>
                        <DollarOutlined style={{ fontSize: 22, color: '#722ed1', marginBottom: 4 }} />
                        <Statistic
                            value={totalGains > 0 ? ((totalPaid / totalGains) * 100).toFixed(1) : 0}
                            suffix="%"
                            valueStyle={{ fontSize: 18, fontWeight: 700, color: '#722ed1' }}
                        />
                        <div style={{ fontSize: 11, color: '#8c8c8c', marginTop: -4 }}>Taux payé</div>
                    </Card>
                </Col>
            </Row>

            {/* Table */}
            <Card
                style={{ borderRadius: 12 }}
                title={
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 12 }}>
                        <span style={{ fontWeight: 600, fontSize: 15 }}>
                            <FilterOutlined style={{ marginRight: 8 }} />
                            Liste des bénéficiaires
                            <Tag style={{ marginLeft: 8, borderRadius: 12 }}>{filteredData.length}</Tag>
                        </span>
                        <Space wrap>
                            <Select
                                placeholder="Type"
                                value={typeFilter}
                                onChange={(value) => setTypeFilter(value)}
                                allowClear
                                size="small"
                                style={{ width: 130 }}
                                options={[
                                    { value: 'affiliate', label: 'Affiliés' },
                                    { value: 'ambassador', label: 'Ambassadeurs' }
                                ]}
                            />
                            <Select
                                placeholder="Statut paiement"
                                value={paymentFilter}
                                onChange={(value) => setPaymentFilter(value)}
                                allowClear
                                size="small"
                                style={{ width: 140 }}
                                options={[
                                    { value: 'paid', label: '✅ Payé' },
                                    { value: 'pending', label: '⏳ En attente' },
                                    { value: 'partial', label: '⚡ Partiel' }
                                ]}
                            />
                            {cityOptions.length >= 1 && (
                                <Select
                                    placeholder="Ville"
                                    value={cityFilter}
                                    onChange={(value) => setCityFilter(value)}
                                    allowClear
                                    size="small"
                                    style={{ width: 130 }}
                                    options={cityOptions}
                                />
                            )}
                            <RangePicker
                                placeholder={['Début', 'Fin']}
                                format="DD/MM/YYYY"
                                value={dateRange}
                                onChange={(dates) => setDateRange(dates)}
                                allowClear
                                size="small"
                                style={{ width: 220 }}
                                presets={[
                                    { label: 'Ce mois', value: [dayjs().startOf('month'), dayjs().endOf('month')] },
                                    { label: 'Mois dernier', value: [dayjs().subtract(1, 'month').startOf('month'), dayjs().subtract(1, 'month').endOf('month')] },
                                    { label: '3 derniers mois', value: [dayjs().subtract(3, 'month').startOf('month'), dayjs()] }
                                ]}
                            />
                            <Input
                                placeholder="Rechercher..."
                                prefix={<SearchOutlined style={{ color: '#bfbfbf' }} />}
                                value={searchText}
                                onChange={(e) => setSearchText(e.target.value)}
                                style={{ width: 180, borderRadius: 8 }}
                                allowClear
                                size="small"
                            />
                        </Space>
                    </div>
                }
                bodyStyle={{ padding: 0 }}
            >
                <Table
                    columns={columns}
                    dataSource={filteredData}
                    rowKey="_id"
                    loading={loading}
                    pagination={{
                        pageSize: 20,
                        showSizeChanger: true,
                        pageSizeOptions: ['10', '20', '50', '100'],
                        showTotal: (total) => `${total} bénéficiaire${total > 1 ? 's' : ''}`,
                        size: 'small',
                        style: { marginRight: 16 }
                    }}
                    scroll={{ x: 1200 }}
                    locale={{ emptyText: <Empty description="Aucun bénéficiaire trouvé" image={Empty.PRESENTED_IMAGE_SIMPLE} /> }}
                    size="middle"
                />
            </Card>
        </div>
    );
};

export default RevenuesPaymentStatusPage;
